import { AxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { RedoOutlined } from '@ant-design/icons';
import { Row, Space, Typography } from 'antd';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import FormAutocomplete from 'components/new-ui/composed/formAutocomplete/FormAutocomplete';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import { IndividualType } from 'store/individuals/individual';
import { DefaultOptionType } from 'antd/lib/select';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import Notification from 'components/ui/base/notification/Notification';
import { assignOwnerToJob } from 'store/jobs/client';
import { getRequiredMessage } from 'views/declarations/utils/validation-utils';
import { Team } from 'store/teams/types';
import { getTeamById } from 'store/teams/client';

enum FieldNames {
    TEAM_MEMBER_ID = 'teamMemberId',
}

type Props = {
    open: boolean;
    jobData: {
        jobId: string;
        teamId: string | null | undefined;
    };
    onClose: () => void;
    onSuccess: () => void;
};

const getValidationSchema = () =>
    Yup.object({
        [FieldNames.TEAM_MEMBER_ID]: Yup.string().nullable().required(getRequiredMessage(FieldNames.TEAM_MEMBER_ID)),
    });

const ALLOWED_ASSIGNEE_ROLES: IndividualType[] = [
    IndividualType.TEAM_MANAGER,
    IndividualType.JOB_MANAGER,
    IndividualType.CLEARANCE_AGENT,
];

const AssignJobModal = ({ onSuccess, open, onClose, jobData }: Props) => {
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();

    const [teamMemberInput, setTeamMemberInput] = useState('');
    const [selectedTeam, setSelectedTeam] = useState<Team>();
    const [filteredTeamMembers, setFilteredTeamMembers] = useState<DefaultOptionType[]>([]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: { teamMemberId: null },
        validationSchema: getValidationSchema(),
        onSubmit: async (values, helpers) => {
            try {
                showGlobalOverlay({ type: 'LoadingOverlay' });

                if (!values.teamMemberId || !jobData.jobId) {
                    throw new Error('Not enough information about the job and the user');
                }

                await assignOwnerToJob(jobData.jobId, values.teamMemberId);
                Notification({
                    type: 'success',
                    placement: 'top',
                    messageTitle: 'Success',
                    description: 'Successfully assigned job',
                });

                setTeamMemberInput('');
                helpers.resetForm();
                onSuccess();
            } catch (error) {
                const erorrInfo = error instanceof AxiosError && error.response?.data.message;
                Notification({
                    type: 'error',
                    placement: 'top',
                    messageTitle: 'Error',
                    description: erorrInfo || 'Could not assign job. Please try again.',
                });
            } finally {
                hideGlobalOverlay();
            }
        },
    });

    const handleConfirm = async () => {
        await formik.submitForm();
        formik.resetForm();
    };

    const teamMemberOptions: DefaultOptionType[] = useMemo(
        () =>
            selectedTeam?.members
                // Display only active team mebers
                .filter((m) => ALLOWED_ASSIGNEE_ROLES.includes(m.role) && m.individual.active)
                .map((m) => ({
                    label: m.individual.fullName || m.individual.email,
                    value: m.individual.id,
                })) ?? [],
        [selectedTeam]
    );

    const handleSearchIndividuals = (val: string) => {
        setTeamMemberInput(val);

        if (formik.values.teamMemberId) {
            formik.setFieldTouched(FieldNames.TEAM_MEMBER_ID, true, false);
            formik.setFieldValue(FieldNames.TEAM_MEMBER_ID, undefined);
        }

        const q = val.trim().toLocaleLowerCase();
        if (!q) {
            setFilteredTeamMembers(teamMemberOptions);
            return;
        }

        setFilteredTeamMembers(teamMemberOptions.filter((option) => String(option.label).toLowerCase().includes(q)));
    };

    const handleSelectIndividual = (val: string, option?: DefaultOptionType) => {
        formik.setFieldValue(FieldNames.TEAM_MEMBER_ID, val);
        setTeamMemberInput(String(option?.label ?? ''));
    };

    const handleCrearIndividual = () => {
        setTeamMemberInput('');
        formik.setFieldValue(FieldNames.TEAM_MEMBER_ID, undefined);
    };

    // Fetch the team from the job
    useEffect(() => {
        // Trigger refetch on the team even if the teamId has not changed
        if (!open) return;

        if (jobData.teamId) {
            getTeamById(jobData.teamId).then((res) => setSelectedTeam(res));
        }
    }, [open, jobData.teamId]);

    useEffect(() => {
        setFilteredTeamMembers(teamMemberOptions);
    }, [teamMemberOptions]);

    return (
        <ConfirmationModal
            destroyOnClose
            open={open}
            onConfirm={handleConfirm}
            icon={<RedoOutlined />}
            title="Assign Job"
            confirmButtonText="Assign"
            onCancel={() => {
                formik.resetForm();
                setTeamMemberInput('');
                onClose();
            }}
            confirmationButtonDisabled={!formik.isValid && formik.dirty}
            content={
                <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
                    <Space
                        direction="vertical"
                        size={16}
                        style={{
                            textAlign: 'left',
                            width: '100%',
                        }}
                    >
                        <Typography.Text>Please select a team and a team member to assign this job to</Typography.Text>
                        <Row justify="space-between">
                            <label htmlFor="team">Team:</label>
                            <div>
                                <FormAutocomplete disabled style={{ minWidth: 375 }} value={selectedTeam?.name ?? ''} />
                            </div>
                        </Row>

                        <Row justify="space-between">
                            <label htmlFor={FieldNames.TEAM_MEMBER_ID}>Team member:</label>
                            <div>
                                <FormAutocomplete
                                    id={FieldNames.TEAM_MEMBER_ID}
                                    style={{ minWidth: 375 }}
                                    inputPlaceholder="Select team member"
                                    fieldMeta={formik.getFieldMeta(FieldNames.TEAM_MEMBER_ID)}
                                    onBlur={() => formik.setFieldTouched(FieldNames.TEAM_MEMBER_ID, true)}
                                    onSearch={handleSearchIndividuals}
                                    onSelect={handleSelectIndividual}
                                    onClear={handleCrearIndividual}
                                    options={filteredTeamMembers}
                                    value={teamMemberInput}
                                />
                            </div>
                        </Row>
                    </Space>
                </form>
            }
        />
    );
};

export default AssignJobModal;
